import { prefersReducedMotion } from '@theme/utilities';

/**
 * Site-wide attribute enhancements, loaded from snippets/scripts.liquid:
 * - `[data-reveal]` gets `is-in` the first time it scrolls into view.
 * - `img.lazy-image` gets `is-loaded` once loaded (or failed), so it can fade in.
 * New markup (theme editor, load more, recommendations) is picked up automatically.
 */

const revealObserver =
  'IntersectionObserver' in window && !prefersReducedMotion()
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            revealObserver?.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

/** @param {ParentNode} root */
function enhance(root) {
  for (const element of root.querySelectorAll('[data-reveal]:not(.is-in)')) {
    if (revealObserver) revealObserver.observe(element);
    else element.classList.add('is-in');
  }
  for (const image of root.querySelectorAll('img.lazy-image:not(.is-loaded)')) {
    if (image instanceof HTMLImageElement && image.complete) image.classList.add('is-loaded');
  }
}

/** @param {Event} event */
function onImageSettled(event) {
  const image = event.target;
  if (image instanceof HTMLImageElement && image.classList.contains('lazy-image')) image.classList.add('is-loaded');
}

// load and error do not bubble: listen in the capture phase
document.addEventListener('load', onImageSettled, true);
document.addEventListener('error', onImageSettled, true);

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('[data-reveal], img.lazy-image')) enhance(/** @type {ParentNode} */ (node.parentElement || node));
      else enhance(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

enhance(document);
